import { Bars3Icon } from "@heroicons/react/20/solid";
import { type ReactNode, useEffect, useState } from "react";
import { JSX } from "react/jsx-runtime";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { atom, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { A } from "@/atoms-react/a/A.react";
import { Button } from "@/atoms-react/button/Button.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base, SectionTitle } from "@/atoms-react/text/Text.react";
import { AccountSelector } from "@/components/account-selector/index.react";
import { useAuth } from "@/features/auth/hooks/use-auth-react";
import { role } from "@/features/auth/types";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import usePartnerChange from "@/features/flow/hooks/use-partner-change";
import { FromURL } from "@/features/flow/types";
import { Routes } from "@/features/router";
import { useAppSelector } from "@/redux/hooks";
import { ADMIN_SOURCE_PAGE } from "@/redux/slices/adminSlice";
import { getUserRoles } from "@/redux/slices/userSlice";
import { Themes } from "@/types/theme";
import { BackButton } from "../../flow/components/flow-header.react";
import { Menu } from "./menu.react";
import { MenuAccount } from "./menu-account.react";
import { MenuAdminAccount } from "./menu-account-admin-react";
import { MenuWallet } from "./menu-wallet.react";

export enum MenuType {
  Account,
  Wallet,
  Main,
  CompanyOnboarding
}

export type LayoutTitleProps = {
  children?: ReactNode;
  back?: boolean;
  action?: ReactNode;
  bgImage?: string;
  bgText?: string;
  bgTitle?: string;
  dataCy?: string;
  actionSupport?: JSX.Element;
  menuType?: MenuType | null;
  className?: string;
};

const SideMenuAtom = atom({
  key: `SideMenuAtom`,
  default: false
});

const SideMenuTypeAtom = atom<MenuType | null>({
  key: `SideMenuTypeAtom`,
  default: null
});

export const LayoutTitle = ({
  children,
  back,
  action,
  dataCy,
  actionSupport,
  menuType = null,
  className = ""
}: LayoutTitleProps) => {
  const { pathname } = useLocation();
  const openMenu = useSetRecoilState(SideMenuAtom);
  const setMenuType = useSetRecoilState(SideMenuTypeAtom);

  const origin = useOrigin();
  useEffect(() => {
    openMenu(false);
    setMenuType(menuType);
  }, [pathname, menuType]);
  return (
    <>
      <div
        className={twMerge(
          "flex w-full flex-col sm:mb-4",
          origin === FromURL.Partner && "flex-col",
          className
        )}
        data-cy={dataCy}
      >
        <div
          className={twMerge(
            "flex w-full flex-col justify-center overflow-hidden text-ellipsis whitespace-nowrap text-left md:flex-row md:justify-between 2xl:text-left"
          )}
        >
          {back && <BackButton className={"mr-4 mb-0"} />}
          <SectionTitle className="block overflow-hidden text-ellipsis whitespace-nowrap 2xl:hidden">
            {children}
          </SectionTitle>

          <SectionTitle
            className={twMerge(
              "hidden w-full overflow-hidden text-ellipsis whitespace-nowrap font-semibold 2xl:block"
            )}
          >
            {children}
          </SectionTitle>
          <div className="mt-3 md:mt-0">{actionSupport}</div>
        </div>
        <div className="mt-4 mr-2 mb-2 flex shrink-0 items-center justify-start sm:mb-0 2xl:hidden">
          <Button
            size="md"
            theme={Themes.iconButton}
            icon={<Bars3Icon />}
            onClick={() => openMenu(true)}
            dataCy="toggle-menu-button"
          />
          <div className="ml-3 w-xs">
            <AccountSelector smallVersion={false} />
          </div>
        </div>
        <div className="items-right mt-2 hidden md:flex md:justify-start 2xl:justify-end">
          {action}
        </div>
      </div>
      <div className={"justify-left flex md:hidden"}>{action}</div>
    </>
  );
};

export const Layout = () => {
  const { user } = useAuth();
  const [open, setOpen] = useRecoilState(SideMenuAtom);
  const forcedMenuType = useRecoilValue(SideMenuTypeAtom);
  const { mutateAsync: updatePartnerChange } = usePartnerChange();
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const isNotMobile = useMediaQuery("(min-width: 768px)");
  const { leUuid } = useCompanyContext();
  const userRoles = useAppSelector(getUserRoles);
  const adminSourcePage = useAppSelector((state) => state.admin.adminSourcePage);

  const [menu, setMenu] = useState<MenuType>(
    location.pathname.includes(Routes.account_base)
      ? MenuType.Account
      : location.pathname.includes(Routes.wallet_base)
        ? MenuType.Wallet
        : MenuType.Main
  );
  const navigate = useNavigate();
  const updatePartner = async (
    partner: FromURL,
    url: string,
    partnerType?: string,
    lang?: string
  ) => {
    setActionLoading(true);
    try {
      await updatePartnerChange({
        partner: partner,
        url,
        partnerType: partnerType as string,
        lang: lang as string
      });
    } catch (_) {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    const defaultMenuType =
      location.pathname.includes(Routes.account_base) ||
      (location.pathname.includes(`related_person`) && userRoles.includes(role.ADMIN))
        ? MenuType.Account
        : location.pathname.includes(Routes.wallet_base)
          ? MenuType.Wallet
          : MenuType.Main;

    setMenu(forcedMenuType ?? defaultMenuType);
  }, [location.pathname, forcedMenuType]);

  if (user.isPending || !user.data?.naturalPerson?.UUID || actionLoading) {
    return (
      <>
        <Loader className="absolute top-0 right-0 bottom-0 left-0 m-auto" />
      </>
    );
  }

  return (
    <div className="flex h-full grow flex-row">
      <div
        className={twMerge(
          "absolute top-0 left-0 z-20 h-full w-full bg-black transition-all",
          open ? "opacity-50" : "pointer-events-none opacity-0"
        )}
        onClick={() => setOpen(false)}
      />

      {(isNotMobile || menu === MenuType.Main) && (
        <Menu
          open={open}
          isAdmin={userRoles.includes(role.ADMIN)}
          updatePartnerChange={updatePartner}
          smallVersion={[MenuType.Account, MenuType.CompanyOnboarding, MenuType.Wallet].includes(
            menu
          )}
          onOlkyPassClick={() => {
            setMenu(MenuType.Account);
            if (isNotMobile && !location.pathname.includes(Routes.level)) {
              navigate(leUuid ? `${Routes.level}/${leUuid}` : Routes.level);
            }
          }}
        />
      )}
      {[MenuType.Account, MenuType.CompanyOnboarding].includes(menu) &&
        !userRoles.includes(role.ADMIN) && (
          <MenuAccount
            isCompanyOnboarding={menu === MenuType.CompanyOnboarding}
            open={open}
            onClose={() => {
              setMenu(MenuType.Main);
              if (isNotMobile) {
                navigate(leUuid ? `${Routes.dashboard}/${leUuid}` : Routes.dashboard);
              }
            }}
            className="absolute left-48"
          />
        )}
      {menu === MenuType.Account && userRoles.includes(role.ADMIN) && (
        <MenuAdminAccount
          open={open}
          onClose={() => {
            setMenu(MenuType.Main);
            if (isNotMobile) {
              const route =
                adminSourcePage === ADMIN_SOURCE_PAGE.NEW ? `${Routes.pass}/new` : Routes.dashboard;

              navigate(route);
            }
          }}
          className="absolute left-48"
        />
      )}
      {menu === MenuType.Wallet && !userRoles.includes(role.ADMIN) && (
        <MenuWallet
          open={open}
          onClose={() => {
            setMenu(MenuType.Main);
            if (isNotMobile) {
              navigate(leUuid ? `${Routes.dashboard}/${leUuid}` : Routes.dashboard);
            }
          }}
          className="absolute left-48"
        />
      )}

      <div className="relative z-10 grow overflow-y-auto overflow-x-hidden px-4 py-4 2xl:py-6">
        <Outlet />
      </div>
    </div>
  );
};

export const MenuItem = ({
  to,
  onClick,
  icon,
  smallVersion,
  children,
  isVisible = true,
  dataCy,
  isActive = false,
  disabled = false,
  ...props
}: {
  to?: string;
  onClick?: () => void;
  icon: ({ className }: { className: string }) => ReactNode;
  smallVersion?: boolean;
  children?: ReactNode | ReactNode[];
  isVisible?: boolean;
  dataCy?: string;
  isActive?: boolean;
  disabled?: boolean;
}) => {
  const { pathname } = useLocation();
  const active = isActive || pathname.indexOf(to || "none") === 0;
  const origin = useOrigin();

  if (!isVisible) return null;

  return (
    <A
      disabled={disabled}
      to={to}
      onClick={onClick}
      noColor
      className={twMerge(
        "flex cursor-pointer flex-row items-center py-2 text-slate-500 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-200",
        active ? "text-primary hover:text-primary dark:text-primary dark:hover:text-primary" : ""
      )}
    >
      <div
        data-cy={dataCy}
        {...props}
        className={twMerge(
          "flex shrink-0 items-center justify-center rounded bg-slate-50 dark:bg-opacity-15",
          active && origin === FromURL.None && "bg-blue-100 dark:bg-white dark:bg-opacity-100",
          active &&
            origin === FromURL.Partner &&
            "bg-primary_menu_item dark:bg-white dark:bg-opacity-100",
          "h-8 w-8"
        )}
      >
        {icon({ className: "h-4 w-4" })}
      </div>
      <div className="w-auto">
        <Base
          className={twMerge(
            "-mb-px ml-2 grow overflow-hidden text-ellipsis whitespace-normal opacity-100 transition-opacity",
            active && "dark:text-white",
            smallVersion && "opacity-0"
          )}
          noColor
        >
          {children}
        </Base>
      </div>
    </A>
  );
};
